/**
 * Team Performance — column model.
 *
 * Same shape as agent-performance/columns.ts (`stickyLeft` pins to the left
 * edge, `locked` means "not hideable") so this report inherits the same
 * table behaviour. Team Name is the pinned identity column, exactly as Agent
 * Name is in Agent Performance.
 */
import {
  Users, Inbox, CheckCircle2, Loader, RotateCcw, ArrowDownLeft, ArrowUpRight,
  StickyNote, MessageSquare, Timer, TimerReset, ShieldCheck, ShieldAlert,
  Percent, PercentCircle,
} from 'lucide-react'
import type { ReportColumnDef } from '../agent-performance/columns.js'

export { formatDuration, formatPercent } from '../agent-performance/columns.js'
export type { ReportColumnDef } from '../agent-performance/columns.js'

/** Report id — the key this report's saved view is stored under on the user. */
export const TEAM_PERFORMANCE_REPORT_ID = 'conversation.team-performance'

export const TEAM_PERFORMANCE_COLUMNS: ReportColumnDef[] = [
  { key: 'teamName', label: 'Team Name', width: 220, stickyLeft: 0, locked: true, icon: Users },
  { key: 'assigned', label: 'Assigned', width: 200, align: 'right', icon: Inbox },
  { key: 'closed', label: 'Closed', width: 200, align: 'right', icon: CheckCircle2 },
  { key: 'workInProgress', label: 'Work in Progress', width: 200, align: 'right', icon: Loader },
  { key: 'reOpened', label: 'Re Opened', width: 200, align: 'right', icon: RotateCcw },
  { key: 'transferToTeam', label: 'Transfer to Team', width: 200, align: 'right', icon: ArrowDownLeft },
  { key: 'transferByTeam', label: 'Transfer By Team', width: 200, align: 'right', icon: ArrowUpRight },
  { key: 'notes', label: 'Notes', width: 200, align: 'right', icon: StickyNote },
  { key: 'responses', label: 'Responses', width: 200, align: 'right', icon: MessageSquare },
  { key: 'avgResponseSeconds', label: 'Average Response Time', width: 300, align: 'right', icon: Timer },
  { key: 'avgResolutionSeconds', label: 'Average Resolution Time', width: 300, align: 'right', icon: TimerReset },
  { key: 'withinResponseSla', label: 'Within Response SLA', width: 300, align: 'right', icon: ShieldCheck },
  { key: 'withinResolutionSla', label: 'Within Resolution SLA', width: 300, align: 'right', icon: ShieldAlert },
  { key: 'responseSlaPct', label: 'Response SLA%', width: 250, align: 'right', icon: Percent },
  { key: 'resolutionSlaPct', label: 'Resolution SLA%', width: 250, align: 'right', icon: PercentCircle },
]

/** Everything visible, in catalog order — what a user sees before saving a view. */
export const DEFAULT_VISIBLE_COLUMNS: string[] = TEAM_PERFORMANCE_COLUMNS.map((c) => c.key)

/**
 * Reconcile a stored view against the current catalog. Unknown keys are
 * dropped, locked columns are forced back in, and an empty result falls back
 * to the default set. See agent-performance/columns.ts for the full rationale
 * — this is the same function, re-keyed to this report's catalog.
 */
export function sanitizeColumnOrder(saved: unknown): string[] {
  if (!Array.isArray(saved)) return DEFAULT_VISIBLE_COLUMNS
  const known = new Set(TEAM_PERFORMANCE_COLUMNS.map((c) => c.key))
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const key of saved) {
    if (typeof key !== 'string' || !known.has(key) || seen.has(key)) continue
    seen.add(key)
    ordered.push(key)
  }
  for (const c of TEAM_PERFORMANCE_COLUMNS) {
    if (c.locked && !seen.has(c.key)) {
      seen.add(c.key)
      ordered.unshift(c.key)
    }
  }
  return ordered.length > 0 ? ordered : DEFAULT_VISIBLE_COLUMNS
}
